import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * An awaitable, drop-in replacement for `window.confirm` backed by
 * `<ConfirmDialog>`.
 *
 * The hook owns the open/closed state and the pending promise; the caller
 * only has to render the dialog when `confirmDialogProps` is non-null.
 *
 * Usage:
 *   const { confirm, confirmDialogProps } = useConfirm();
 *
 *   const handleDelete = async () => {
 *     if (!await confirm('Archive this mouse?', { confirmLabel: 'Yes, Archive', danger: true })) return;
 *     // ...
 *   };
 *
 *   // In JSX:
 *   {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
 *
 * @returns {{ confirm: (message: string, options?: object) => Promise<boolean>,
 *             confirmDialogProps: object|null }}
 */
export function useConfirm() {
  const [dialog, setDialog] = useState(null);
  // The pending promise's `resolve`, kept in a ref so onConfirm/onCancel stay
  // referentially stable across renders.
  const resolveRef = useRef(null);

  const settle = useCallback((answer) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setDialog(null);
    if (resolve) resolve(answer);
  }, []);

  const confirm = useCallback((message, options = {}) => new Promise(resolve => {
    // A second confirm opened while one is still pending cancels the first,
    // rather than leaving its awaiting handler hanging forever.
    if (resolveRef.current) {
      const stale = resolveRef.current;
      resolveRef.current = null;
      stale(false);
    }
    resolveRef.current = resolve;
    setDialog({ message, ...options });
  }), []);

  // Same reasoning on unmount: unwind the awaiting handler with a "no"
  // instead of stranding it.
  useEffect(() => () => {
    const pending = resolveRef.current;
    resolveRef.current = null;
    if (pending) pending(false);
  }, []);

  const onConfirm = useCallback(() => settle(true), [settle]);
  const onCancel = useCallback(() => settle(false), [settle]);

  return {
    confirm,
    confirmDialogProps: dialog ? { ...dialog, onConfirm, onCancel } : null,
  };
}

export default useConfirm;
